/* eslint-disable react/no-array-index-key */
import { CSSProperties, FC } from 'react';
import customTwMerge from 'twMerge';

import Heading from 'molecules/Heading';
import { splitSectionProps } from 'molecules/Section/config';

import { ContentTableConfigProps } from 'components/Tables/ContentTable/config';

const DEFAULT_COL_HEADER_BG = '#f6f6ff';
const DEFAULT_ROW_HEADER_BG = '#fafafe';
const DEFAULT_CELL_BG = '#ffffff';

const getContrastColor = (hexColor?: string): string => {
  if (!hexColor || !hexColor.startsWith('#') || hexColor.length < 7) {
    return '#101017';
  }
  const r = parseInt(hexColor.slice(1, 3), 16);
  const g = parseInt(hexColor.slice(3, 5), 16);
  const b = parseInt(hexColor.slice(5, 7), 16);
  const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;

  return luminance > 0.5 ? '#101017' : '#ffffff';
};

const ContentTable: FC<ContentTableConfigProps> = props => {
  const { rest } = splitSectionProps(props);
  const {
    heading,
    headingTag = 'h3',
    headingAlignment = 'left',
    columnHeaders,
    rows,
    hasColumnHeaders = true,
    hasRowHeaders = true,
    columnHeaderBgColor = DEFAULT_COL_HEADER_BG,
    columnHeaderFontSize = 'text-sm',
    columnHeaderFontWeight = 'font-bold',
    rowHeaderBgColor = DEFAULT_ROW_HEADER_BG,
    rowHeaderFontSize = 'text-sm',
    rowHeaderFontWeight = 'font-semibold',
    cellBgColor = DEFAULT_CELL_BG,
    cellFontSize = 'text-sm',
    cellFontWeight = 'font-medium',
  } = rest;

  if (!rows || rows.length === 0) {
    return null;
  }

  const showColumnHeaders = hasColumnHeaders && columnHeaders && columnHeaders.length > 0;

  const isCustomColHeader = columnHeaderBgColor !== DEFAULT_COL_HEADER_BG;
  const isCustomRowHeader = rowHeaderBgColor !== DEFAULT_ROW_HEADER_BG;
  const isCustomCell = cellBgColor !== DEFAULT_CELL_BG;

  // Only apply inline styles when user has set a custom color; otherwise let Tailwind dark: handle it
  const colHeaderStyle: CSSProperties = isCustomColHeader
    ? { backgroundColor: columnHeaderBgColor, color: getContrastColor(columnHeaderBgColor) }
    : {};

  const rowHeaderStyle: CSSProperties = isCustomRowHeader
    ? { backgroundColor: rowHeaderBgColor, color: getContrastColor(rowHeaderBgColor) }
    : {};

  const cellStyle: CSSProperties = isCustomCell
    ? { backgroundColor: cellBgColor, color: getContrastColor(cellBgColor) }
    : {};

  const colHeaderThemeClasses = !isCustomColHeader ? 'bg-gray-50 text-gray-900 dark:bg-gray-700 dark:text-white' : '';

  const rowHeaderThemeClasses = !isCustomRowHeader
    ? 'bg-gray-25 text-gray-900 dark:bg-gray-700 dark:text-gray-300'
    : '';

  const cellThemeClasses = !isCustomCell ? 'bg-white text-gray-800 dark:bg-gray-800 dark:text-gray-300' : '';

  return (
    <>
      {heading && (
        <div className="mb-6">
          <Heading
            heading={heading}
            as={headingTag as 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6'}
            alignment={headingAlignment as 'left' | 'center'}
          />
        </div>
      )}

      <div className="overflow-hidden rounded-xl border border-gray-200 dark:border-gray-900">
        <table className="w-full table-fixed border-collapse">
          {showColumnHeaders && (
            <thead>
              <tr>
                {hasRowHeaders && (
                  <th
                    className={customTwMerge(
                      'w-[35%] border-b border-r border-gray-200 px-3 py-3 md:w-auto md:px-5 md:py-4 dark:border-gray-900',
                      colHeaderThemeClasses,
                    )}
                    style={colHeaderStyle}
                  />
                )}
                {columnHeaders!.map((col, idx) => (
                  <th
                    key={col.id}
                    className={customTwMerge(
                      'hyphens-auto border-b border-gray-200 px-3 py-3 text-center md:px-5 md:py-4 dark:border-gray-900',
                      columnHeaderFontSize,
                      columnHeaderFontWeight,
                      idx < columnHeaders!.length - 1 && 'border-r border-gray-200 dark:border-gray-900',
                      colHeaderThemeClasses,
                    )}
                    style={colHeaderStyle}
                  >
                    {col.label}
                  </th>
                ))}
              </tr>
            </thead>
          )}
          <tbody>
            {rows.map((row, rowIdx) => {
              const isLastRow = rowIdx === rows.length - 1;

              return (
                <tr key={row.id}>
                  {hasRowHeaders && (
                    <th
                      className={customTwMerge(
                        'w-[35%] hyphens-auto border-r border-gray-200 px-3 py-3 text-left align-top md:w-auto md:px-5 md:py-4 dark:border-gray-900',
                        rowHeaderFontSize,
                        rowHeaderFontWeight,
                        !isLastRow && 'border-b border-gray-200 dark:border-gray-900',
                        rowHeaderThemeClasses,
                      )}
                      style={rowHeaderStyle}
                      scope="row"
                    >
                      {row.rowHeader}
                    </th>
                  )}
                  {row.cells?.map((cell, idx) => (
                    <td
                      key={idx}
                      className={customTwMerge(
                        'hyphens-auto px-3 py-3 align-top md:px-5 md:py-4',
                        cellFontSize,
                        cellFontWeight,
                        !isLastRow && 'border-b border-gray-200 dark:border-gray-900',
                        idx < (row.cells?.length ?? 0) - 1 && 'border-r border-gray-200 dark:border-gray-900',
                        cellThemeClasses,
                      )}
                      style={cellStyle}
                    >
                      {cell.text}
                    </td>
                  ))}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </>
  );
};

export default ContentTable;
